import { useEffect, useRef, useState } from "react";

const missions = [
	{
		number: "01",
		title: "すべての音楽家に寄り添ったWebサービスを",
		description:
			"演奏家、作曲家、イベント主催者。あらゆる音楽家が本当に必要としている機能を、丁寧につくります。",
	},
	{
		number: "02",
		title: "革新的なサービスをリーズナブルな料金で",
		description:
			"最新のテクノロジーを活用しながらも、誰もが気軽に使える価格帯を維持します。",
	},
	{
		number: "03",
		title: "あらゆる操作をかんたんに",
		description:
			"テクノロジーに不慣れな方でも迷わず使える、直感的な操作性を追求します。",
	},
] as const;

export default function AboutSection() {
	const sectionRef = useRef<HTMLElement>(null);
	const [parallax, setParallax] = useState(0);

	useEffect(() => {
		let animationFrameId = 0;

		const handleScroll = () => {
			if (animationFrameId) {
				return;
			}

			animationFrameId = window.requestAnimationFrame(() => {
				if (!sectionRef.current) {
					animationFrameId = 0;
					return;
				}

				const rect = sectionRef.current.getBoundingClientRect();
				const windowHeight = window.innerHeight;
				const sectionHeight = rect.height;

				if (rect.top < windowHeight && rect.bottom > 0) {
					const scrollProgress =
						(windowHeight - rect.top) / (windowHeight + sectionHeight);
					setParallax((scrollProgress - 0.5) * 40);
				}

				animationFrameId = 0;
			});
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll();

		return () => {
			window.removeEventListener("scroll", handleScroll);
			if (animationFrameId) {
				window.cancelAnimationFrame(animationFrameId);
			}
		};
	}, []);

	return (
		<section ref={sectionRef} id="about" className="relative bg-black">
			{/* Content */}
			<div className="py-28 md:py-40">
				<div className="container mx-auto px-6">
					<div className="max-w-3xl mx-auto">
						{/* Header */}
						<p className="text-xs tracking-[0.3em] uppercase text-neutral-500 mb-4">
							About
						</p>
						<h2 className="text-3xl md:text-4xl font-light text-white tracking-tight mb-12">
							私たちの想い
						</h2>

						{/* Image */}
						<div className="overflow-hidden rounded-lg mb-16">
							<img
								className="w-full h-48 md:h-64 object-cover"
								src="/images/welcome/welcome-about-01.jpg"
								alt="About"
								loading="lazy"
								decoding="async"
								style={{
									transform: `translateY(${parallax * 0.3}px)`,
								}}
							/>
						</div>

						{/* Statement */}
						<p className="text-xl md:text-2xl text-white font-light leading-[1.8] tracking-tight mb-10">
							音楽にまつわるすべてを再発明することをテーマに、
							<span className="text-neutral-500">
								Webと音楽の融合によって新たな価値を創出します。
							</span>
						</p>

						{/* Body */}
						<div className="space-y-5 mb-28">
							<p className="text-sm text-neutral-400 leading-[2]">
								音楽を愛するすべての人々に向けて、最新のテクノロジーと革新的なサービスを提供し、音楽を次の次元へと導きます。
							</p>
							<p className="text-sm text-neutral-400 leading-[2]">
								私たちは、音楽の持つ力を最大限に引き出し、アーティスト、ファン、そして全ての音楽愛好家が一体となるプラットフォームを構築しています。創造性と情熱が交差するこの場所で、音楽の未来を共に描き出しましょう。
							</p>
						</div>

						{/* Mission */}
						<p className="text-xs tracking-[0.3em] uppercase text-neutral-500 mb-4">
							Mission
						</p>
						<h3 className="text-2xl md:text-3xl font-light text-white tracking-tight mb-12">
							私たちが目指すもの
						</h3>

						<div className="divide-y divide-white/[0.06] border-t border-white/[0.06]">
							{missions.map((mission) => (
								<div key={mission.number} className="py-8">
									<div className="flex items-start gap-6">
										<span className="text-[11px] tracking-[0.2em] text-neutral-600 font-light pt-1 shrink-0">
											{mission.number}
										</span>
										<div>
											<h4 className="text-base text-white font-light tracking-tight leading-relaxed mb-2">
												{mission.title}
											</h4>
											<p className="text-sm text-neutral-500 leading-[1.8]">
												{mission.description}
											</p>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
